import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, X, RefreshCw } from 'lucide-react';
import { useRelayStatus } from '@/hooks/useRelayStatus';
import { useNodeStatus, type NodeStatus } from '@/hooks/useNodeStatus';
import { DEFAULT_NODES, type MoneroNode } from '@/lib/monero/nodes';

interface StatusBarProps {
  /** Whether to show the mint listener status */
  showMintStatus?: boolean;
  /** Mint listener is active */
  mintListening?: boolean;
  /** Monero node URI (null if wallet mode with no config) */
  moneroNodeUri?: string | null;
  /** Callback for adding a Nostr relay */
  onAddRelay?: (url: string) => void;
  /** Callback for removing a Nostr relay */
  onRemoveRelay?: (url: string) => void;
  /** Callback for changing the Monero node */
  onChangeNode?: (uri: string) => void;
}

function StatusDot({ status }: { status: NodeStatus | 'online' | 'connecting' | 'offline' | boolean }) {
  const color =
    status === true || status === 'online' ? 'bg-green-500' :
    status === 'connecting' ? 'bg-yellow-500' :
    'bg-red-500';
  return <div className={`w-2 h-2 rounded-full shrink-0 ${color}`} />;
}

export function StatusBar({
  showMintStatus,
  mintListening,
  moneroNodeUri,
  onAddRelay,
  onRemoveRelay,
  onChangeNode,
}: StatusBarProps) {
  const { relays, overallStatus: relayOverall, checkRelays } = useRelayStatus();
  const { status: xmrStatus, height: xmrHeight } = useNodeStatus(moneroNodeUri ?? null);
  const [newRelayUrl, setNewRelayUrl] = useState('');
  const [newNodeUri, setNewNodeUri] = useState('');

  const xmrNodeName = moneroNodeUri
    ? DEFAULT_NODES.find(n => `${n.host}:${n.port}` === moneroNodeUri.replace(/^https?:\/\//, ''))
      ?.name ?? moneroNodeUri.replace(/^https?:\/\//, '').split(':')[0]
    : 'None';

  return (
    <div className="flex items-center gap-3 px-4 py-1.5 bg-muted/30 border-b text-xs text-muted-foreground flex-wrap">
      {/* Nostr relays */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="flex items-center gap-1.5 hover:text-foreground transition-colors">
            <StatusDot status={relayOverall} />
            <span>Relays: {relays.filter(r => r.status === 'online').length}/{relays.length}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-3">
            <p className="text-sm font-medium">Nostr Relays</p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {relays.map((r) => (
                <div key={r.url} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <StatusDot status={r.status} />
                    <span className="text-xs truncate">{r.url}</span>
                  </div>
                  {onRemoveRelay && (
                    <button onClick={() => onRemoveRelay(r.url)} className="shrink-0 hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {onAddRelay && (
              <div className="flex gap-2">
                <Input
                  value={newRelayUrl}
                  onChange={(e) => setNewRelayUrl(e.target.value)}
                  placeholder="wss://relay.example.com"
                  className="text-xs h-8"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  onClick={() => {
                    if (newRelayUrl.trim()) {
                      onAddRelay(newRelayUrl.trim());
                      setNewRelayUrl('');
                    }
                  }}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            )}
            <Button size="sm" variant="ghost" className="w-full text-xs" onClick={checkRelays}>
              <RefreshCw className="h-3 w-3 mr-1" /> Refresh
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Monero node */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="flex items-center gap-1.5 hover:text-foreground transition-colors">
            <StatusDot status={xmrStatus} />
            <span>XMR: {xmrNodeName}{xmrHeight > 0 ? ` (${xmrHeight.toLocaleString()})` : ''}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-3">
            <p className="text-sm font-medium">Monero Nodes</p>
            <div className="space-y-1.5">
              {DEFAULT_NODES.map((n) => {
                const uri = `${n.host}:${n.port}`;
                const isActive = moneroNodeUri?.includes(n.host) ?? false;
                return (
                  <div key={uri} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <StatusDot status={isActive ? xmrStatus : 'offline'} />
                      <span className="text-xs">{n.name}</span>
                    </div>
                    {onChangeNode && (
                      <Button
                        size="sm"
                        variant={isActive ? 'default' : 'outline'}
                        className="text-[10px] h-6"
                        onClick={() => onChangeNode(`http://${uri}`)}
                        disabled={isActive}
                      >
                        {isActive ? 'Active' : 'Switch'}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
            {onChangeNode && (
              <div className="flex gap-2">
                <Input
                  value={newNodeUri}
                  onChange={(e) => setNewNodeUri(e.target.value)}
                  placeholder="host:port"
                  className="text-xs h-8"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  onClick={() => {
                    if (newNodeUri.trim()) {
                      onChangeNode(`http://${newNodeUri.trim()}`);
                      setNewNodeUri('');
                    }
                  }}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Mint listener status */}
      {showMintStatus && (
        <div className="flex items-center gap-1.5 ml-auto">
          <StatusDot status={mintListening ?? false} />
          <span>Mint: {mintListening ? 'Live' : 'Stopped'}</span>
        </div>
      )}
    </div>
  );
}
